import { useMemo, useState } from 'react';
import { Api } from '../../application/panelApi';
import { toast } from '../../components/ToastHost';
import { errText, fmtDate, plural } from '../../lib/format';
import {
  CORES,
  DEFAULT_TEMPLATE,
  MEMORY,
  PORT_PRESET,
  TIMEZONES,
  addPorts,
  canonical,
  fromForm,
  previewRows,
  toForm,
  type ChipItem,
  type ProfileTemplate,
  type TemplateForm,
} from './model';
import { Chips } from './shared';
import type { ProfileTools } from './useProfileTools';

const CORE_ITEMS: ChipItem[] = CORES.map((n) => [String(n), String(n)]);
const MEMORY_ITEMS: ChipItem[] = MEMORY.map((n) => [String(n), `${n} ГБ`]);

type TemplateTabProps = {
  active: boolean;
  tools: ProfileTools;
};

export function TemplateTab({ active, tools }: TemplateTabProps) {
  const { template, setTemplate, form, setForm, engineChromium, donors, error } = tools;
  const [portIn, setPortIn] = useState('');

  const patch = (p: Partial<TemplateForm>) => setForm((f) => ({ ...f, ...p }));
  const edge = form.brand === 'Edge';

  const verMode = edge ? 'native' : form.brandVersion.mode;
  const body = useMemo(
    () => fromForm({ ...form, brandVersion: { ...form.brandVersion, mode: verMode } }),
    [form, verMode],
  );

  const dirty = template !== null && canonical(body) !== canonical(template);
  const saved = error
    ? error
    : dirty
      ? 'Есть несохранённые изменения. Нажмите «Сохранить», чтобы применить их.'
      : template?.updatedAt
        ? `Последнее сохранение: ${fmtDate(template.updatedAt)} UTC.`
        : '';

  const lib = form.hardware.source === 'library';
  const hwHint = donors.length
    ? `В библиотеке ${donors.length} ${plural(donors.length, 'отпечаток', 'отпечатка', 'отпечатков')}. Новый профиль получит железо случайного из них с подходящей ОС; если такого нет, параметры сгенерируются по seed.`
    : 'Библиотека отпечатков пуста, поэтому параметры железа генерируются по seed.';

  const addPort = () => {
    const v = Number(portIn);
    if (!Number.isInteger(v) || v < 1 || v > 65535) {
      toast('Неверный номер порта. Введите целое число от 1 до 65535.', 'err');
      return;
    }
    patch({ blockedPorts: addPorts(form.blockedPorts, [v]) });
    setPortIn('');
  };

  const save = async () => {
    const f = body;
    if (f.brandVersion.mode !== 'native' && !f.brandVersion.values.length) return toast('Не указаны версии браузера. Впишите хотя бы одну в поле «Версии» или выберите «Как у движка».', 'err');
    if (f.timezone.mode === 'fixed' && !f.timezone.value) return toast('Не указан часовой пояс. Заполните поле «Пояс» или выберите «По гео прокси».', 'err');
    if (f.language.mode === 'fixed' && !f.language.value) return toast('Не указан язык. Заполните поле «Языки» или выберите «По гео прокси».', 'err');
    try {
      const data = (await Api.profileTemplate.save(f)) as { template: ProfileTemplate; changed?: boolean };
      setTemplate(data.template);
      setForm(toForm(data.template));
      toast(
        data.changed
          ? 'Шаблон сохранён. Ноды получат его примерно за 10 секунд; уже созданные профили не изменятся.'
          : 'Шаблон не изменился, сохранять нечего.',
        'ok',
      );
    } catch (err) {
      toast(errText(err), 'err');
    }
  };

  const reset = () => {
    setForm(toForm(DEFAULT_TEMPLATE));
    toast('В форму подставлены значения по умолчанию. Нажмите «Сохранить», чтобы применить их к новым профилям.');
  };

  const preview = previewRows(body, engineChromium);

  return (
    <div className={`pr-pane${active ? '' : ' hidden'}`} data-prpane="template">
      <div className="grid-warm pr-grid">
        <div className="sec">
          <h2>
            <span className="sq" />
            Шаблон новых профилей
          </h2>

          <div className="pr-group">Браузер</div>
          <div className="grid2">
            <div className="field-row">
              <label htmlFor="ptEngine">Движок</label>
              <input
                type="text"
                id="ptEngine"
                disabled
                value={engineChromium ? `Clearcote Chromium ${engineChromium}` : 'Clearcote Chromium'}
                readOnly
              />
            </div>
            <div className="field-row">
              <label htmlFor="ptBrand">Бренд в UA</label>
              <select
                id="ptBrand"
                value={form.brand}
                onChange={(e) => {
                  const brand = e.target.value;
                  patch(
                    brand === 'Edge'
                      ? { brand, brandVersion: { ...form.brandVersion, mode: 'native' } }
                      : { brand },
                  );
                }}
              >
                <option value="Chrome">Google Chrome</option>
                <option value="Edge">Microsoft Edge</option>
              </select>
            </div>
          </div>
          <div className="grid2">
            <div className="field-row">
              <label htmlFor="ptVerMode">Версия браузера</label>
              <select
                id="ptVerMode"
                value={verMode}
                disabled={edge}
                title={edge ? 'Для Microsoft Edge версия всегда совпадает с версией движка, иначе браузер выглядит неправдоподобно.' : ''}
                onChange={(e) => patch({ brandVersion: { ...form.brandVersion, mode: e.target.value } })}
              >
                <option value="native">Как у движка (рекомендуется)</option>
                <option value="fixed">Фиксированная</option>
                <option value="random">Случайная из списка</option>
              </select>
            </div>
            <div className="field-row">
              <label htmlFor="ptVerValues">Версии</label>
              <input
                type="text"
                id="ptVerValues"
                placeholder="Например: 149, 150.0.7871.114"
                spellCheck={false}
                disabled={verMode === 'native'}
                value={form.brandVersion.text}
                onChange={(e) => patch({ brandVersion: { ...form.brandVersion, text: e.target.value } })}
              />
            </div>
          </div>
          <p className="hint pr-warn" id="ptVerWarn" style={{ display: verMode === 'native' ? 'none' : '' }}>
            Подмена версии меняет только то, как браузер представляется сайтам (User-Agent, Client Hints, TLS). Сам
            движок остаётся прежним, и сайты-чекеры могут заметить расхождение. Безопасно отклоняться от версии движка
            не больше чем на 1–2 основные версии.
          </p>
          <div className="field-row">
            <label htmlFor="ptPlatform">Операционная система</label>
            <select id="ptPlatform" value={form.platform} onChange={(e) => patch({ platform: e.target.value })}>
              <option value="android">Android (Mobile)</option>
              <option value="random">Случайная (Windows ~75%, macOS ~18%, Linux ~7%)</option>
              <option value="windows">Windows</option>
              <option value="macos">macOS</option>
              <option value="linux">Linux</option>
            </select>
          </div>

          <div className="pr-group">Локализация</div>
          <div className="grid2">
            <div className="field-row">
              <label htmlFor="ptTzMode">Часовой пояс</label>
              <select
                id="ptTzMode"
                value={form.timezone.mode}
                onChange={(e) => patch({ timezone: { ...form.timezone, mode: e.target.value } })}
              >
                <option value="auto">По гео прокси</option>
                <option value="fixed">Фиксированный</option>
              </select>
            </div>
            <div className="field-row">
              <label htmlFor="ptTz">Пояс</label>
              <input
                type="text"
                id="ptTz"
                list="ptTzList"
                placeholder="Например: Europe/Moscow"
                spellCheck={false}
                disabled={form.timezone.mode === 'auto'}
                value={form.timezone.value}
                onChange={(e) => patch({ timezone: { ...form.timezone, value: e.target.value } })}
              />
            </div>
          </div>
          <datalist id="ptTzList">
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz} />
            ))}
          </datalist>
          <div className="grid2">
            <div className="field-row">
              <label htmlFor="ptLangMode">Язык браузера</label>
              <select
                id="ptLangMode"
                value={form.language.mode}
                onChange={(e) => patch({ language: { ...form.language, mode: e.target.value } })}
              >
                <option value="auto">По гео прокси</option>
                <option value="fixed">Фиксированный</option>
              </select>
            </div>
            <div className="field-row">
              <label htmlFor="ptLang">Языки (Accept-Language)</label>
              <input
                type="text"
                id="ptLang"
                placeholder="Например: ru-RU,ru,en-US,en"
                spellCheck={false}
                disabled={form.language.mode === 'auto'}
                value={form.language.value}
                onChange={(e) => patch({ language: { ...form.language, value: e.target.value } })}
              />
            </div>
          </div>

          <div className="pr-group">Отпечаток</div>
          <div className="set-row">
            <div className="txt">
              <b>Шум</b>
              <small>
                Добавляет к canvas, WebGL и audio незаметный шум, чтобы у каждого профиля был свой постоянный отпечаток.
                Если выключить, все профили на одном сервере будут выглядеть как одно устройство.
              </small>
            </div>
            <label className="switch">
              <input type="checkbox" id="ptNoise" checked={form.noise} onChange={(e) => patch({ noise: e.target.checked })} />
              <i />
            </label>
          </div>
          <div className="grid2">
            <div className="field-row">
              <label htmlFor="ptCanvas">Canvas</label>
              <select
                id="ptCanvas"
                value={form.canvas}
                disabled={!form.noise}
                onChange={(e) => patch({ canvas: e.target.value })}
              >
                <option value="auto">Авто (шум)</option>
                <option value="real">Реальный</option>
              </select>
            </div>
            <div className="field-row">
              <label htmlFor="ptWebgl">WebGL / GPU</label>
              <select id="ptWebgl" value={form.webgl} onChange={(e) => patch({ webgl: e.target.value })}>
                <option value="auto">Авто (движок / донор)</option>
                <option value="real">Реальный, без подмены</option>
              </select>
            </div>
          </div>
          <div className="pr-fixed">
            <div>
              <b>Fonts</b>
              <span>по ОС профиля (или донора), отдельно не настраивается</span>
            </div>
            <div>
              <b>Audio</b>
              <span>следует за «Шум»</span>
            </div>
            <div>
              <b>Client rects</b>
              <span>всегда реальные: в движке шум rects убран, он детектится</span>
            </div>
            <div>
              <b>Sensors</b>
              <span>только для мобильных профилей - в движке пока нет</span>
            </div>
          </div>
          <p className="hint pr-warn" id="ptGpuWarn" style={{ display: form.webgl === 'real' ? '' : 'none' }}>
            Режим «Реальный» отключает подмену видеокарты даже у профилей с донором. На серверах обычно нет настоящей
            видеокарты (видна программная SwiftShader или llvmpipe), и сайты сразу распознают бота. Включайте, только если
            на нодах стоит настоящая видеокарта.
          </p>

          <div className="pr-group">Железо</div>
          <div className="field-row">
            <label htmlFor="ptHwSource">Источник</label>
            <select
              id="ptHwSource"
              value={form.hardware.source}
              onChange={(e) => patch({ hardware: { ...form.hardware, source: e.target.value } })}
            >
              <option value="seed">Генератор по seed</option>
              <option value="library">Реальные отпечатки из библиотеки</option>
            </select>
            {lib && (
              <span className="hint" id="ptHwHint">
                {hwHint}
              </span>
            )}
          </div>
          <div className="field-row" id="ptHwRow" style={{ display: lib ? 'none' : '' }}>
            <label>CPU cores</label>
            <Chips
              id="ptCores"
              name="cores"
              items={CORE_ITEMS}
              selected={form.hardware.cores.map(String)}
              onChange={(v) => patch({ hardware: { ...form.hardware, cores: v.map(Number) } })}
            />
            <label style={{ marginTop: 6 }}>RAM, ГБ</label>
            <Chips
              id="ptMemory"
              name="memory"
              items={MEMORY_ITEMS}
              selected={form.hardware.memory.map(String)}
              onChange={(v) => patch({ hardware: { ...form.hardware, memory: v.map(Number) } })}
            />
          </div>

          <div className="pr-group">Приватность и сеть</div>
          <div className="field-row">
            <label htmlFor="ptWebrtc">WebRTC</label>
            <select id="ptWebrtc" value={form.webrtc} onChange={(e) => patch({ webrtc: e.target.value })}>
              <option value="proxy">Только через прокси (показывать IP прокси)</option>
              <option value="disable_udp">Отключить non-proxied UDP</option>
              <option value="default">Авто (как в обычном браузере)</option>
            </select>
          </div>
          <div className="field-row">
            <label htmlFor="ptPortIn">Блокировка портов localhost</label>
            <div className="pr-port-add">
              <input
                type="number"
                id="ptPortIn"
                min={1}
                max={65535}
                placeholder="Например: 3389"
                value={portIn}
                onChange={(e) => setPortIn(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addPort();
                  }
                }}
              />
              <button type="button" className="btn" id="ptPortAdd" onClick={addPort}>
                Добавить
              </button>
              <button
                type="button"
                className="btn"
                id="ptPortPreset"
                title="Добавить порты программ удалённого доступа (RDP, VNC, TeamViewer, AnyDesk), которые чаще всего проверяют антифрод-скрипты."
                onClick={() => patch({ blockedPorts: addPorts(form.blockedPorts, PORT_PRESET) })}
              >
                Типовые
              </button>
            </div>
            <div className="pr-chips" id="ptPorts">
              {form.blockedPorts.length ? (
                form.blockedPorts.map((p) => (
                  <span className="pr-chip port" key={p}>
                    {p}
                    <button
                      type="button"
                      data-port={p}
                      aria-label={`Убрать ${p}`}
                      onClick={() => patch({ blockedPorts: form.blockedPorts.filter((x) => x !== p) })}
                    >
                      ×
                    </button>
                  </span>
                ))
              ) : (
                <span className="hint">Список пуст, порты не блокируются.</span>
              )}
            </div>
          </div>

          <div className="sec-foot">
            <span className="hint" id="ptSaved">
              {saved}
            </span>
            <button type="button" className="btn" id="ptReset" onClick={reset}>
              Сбросить к умолчаниям
            </button>
            <button type="button" className="btn btn-primary" id="ptSave" onClick={() => void save()}>
              Сохранить
            </button>
          </div>
        </div>

        <div className="sec">
          <h2>
            <span className="sq" />
            Предпросмотр
          </h2>
          <div id="ptPreview" className="pr-kv">
            {preview.map(([k, v]) => (
              <PreviewRow key={k} k={k} v={v} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewRow({ k, v }: { k: string; v: string }) {
  return (
    <>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </>
  );
}